import { DELAYED_SHIPPING_EXPIRATION } from '../constants/bucket';
import { TITLE } from '../constants/oneLinkKeys';
import { ENGAGEMENT_SOURCE_TYPE } from '../constants/reportsKeys';
import {
  DELAYED_SHIPPING_URL,
  DISABLE_EMAIL_NOTIFICATIONS,
  DISABLE_RECIPIENT_SHIPPED_EMAIL,
  RECEIVER_ADDRESS1,
  RECEIVER_ADDRESS2,
  RECEIVER_CITY,
  RECEIVER_COMPANY_NAME,
  RECEIVER_COUNTRY,
  RECEIVER_FIRST_NAME,
  RECEIVER_LAST_NAME,
  RECEIVER_PHONE,
  RECEIVER_STATE,
  RECEIVER_ZIP,
  SHIPPING_OPTION,
  TRACKING_URL,
} from '../constants/shipping';
import { TEngagementCandidate, TMarketplaceCandidateField } from './bucket';
import { ICampaignItem, ICampaignSummary, IFetchCampaignSummariesResponsePayload } from './campaigns';
import { TMarketplaceSendMap } from './marketplace';
import { IPromiseCallbacks } from './redux';
import { FilterTypesEnum, IApiError, IDatePickerOutput } from './shell';
import { ShippingOptionsEnum } from './shipping';
import { ITimezoneMetadata } from './timezone';

export enum ReportTypesEnum {
  Regular = 'regular',
  Scheduled = 'scheduled',
}

export enum ReportStatusEnum {
  // physical sends (only used in code are listed here, full list is in constants)
  AwaitingAddress = 'awaiting_address',
  Viewed = 'viewed',
  Production = 'production',
  Delivered = 'delivered',
  Expired = 'expired',
  Processing = '',
  AwaitingShipment = 'awaiting_shipment',
  Canceled = 'canceled',
  Completed = 'completed',
  // digital (only used in code are listed here, full list is in constants)
  New = 'New',
  GiftClaimed = 'Gift Claimed',
  GiftExpired = 'Gift Expired',
  Failed = 'failed',
}

export enum EngagementSourceTypesEnum {
  Zapier = 'zapier',
  OneLink = 'one_link',
  DirectSend = 'direct_send',
  Bulk = 'bulk_send',
  HubSpot = 'hubspot',
  Empty = '',
}

export enum ReportSourceTypesEnum {
  Single = 'regular',
  Direct = 'direct_send',
  Bulk = 'bulk_send',
  OneLink = 'one_link',
  Zapier = 'zapier',
  Webhook = 'webhook_api',
  NewHireCampaign = 'ac_new_hire',
  BirthdayCampaign = 'ac_birthday',
  AnniversaryCampaign = 'ac_anniversary',
  HubSpot = 'hubspot',
  Empty = '',
}

export enum ReportsRequestTypesEnum {
  Campaign = 'campaign',
  Product = 'product',
}

export enum EditSendModeEnum {
  Details = 'edit-details',
  Items = 'edit-items',
  Shipping = 'edit-shipping',
  PickCampaigns = 'edit-pick-campaigns',
}

export enum EngagementReminderIntervalsEnum {
  Weekly = 'weekly',
  Biweekly = 'bi-weekly',
  Custom = 'custom',
}

export const REMINDER_INTERVAL_LABELS = {
  [EngagementReminderIntervalsEnum.Weekly]: 'Once a week',
  [EngagementReminderIntervalsEnum.Biweekly]: 'Twice a week',
  [EngagementReminderIntervalsEnum.Custom]: 'Custom',
};

export enum EngagementReminderWeekdaysEnum {
  Sun = 'Sun',
  Mon = 'Mon',
  Tue = 'Tue',
  Wed = 'Wed',
  Thu = 'Thu',
  Fri = 'Fri',
  Sat = 'Sat',
}

export interface IReportEngagementItem extends ICampaignItem {
  digital_claim_date: string;
  digital_claim_status: string;
  digital_gift_expiration: string;
  digital_gift_url: string;
  rybbon_ref_code: string;
  fulfillment_center_id: string;
}

export interface IReport
  extends Omit<
      Required<TEngagementCandidate>,
      'items' | 'pick_campaigns' | 'status' | 'is_office_dropship_enabled' | TMarketplaceCandidateField
    >,
    Pick<TEngagementCandidate, TMarketplaceCandidateField> {
  created_by: string;
  canceled_by: string;
  canceled_at: string;
  [ENGAGEMENT_SOURCE_TYPE]: EngagementSourceTypesEnum;
  on_hold_until: string;
  created_at: string;
  receiver_next_reminder_send_date: string;
  order_id: string;
  woocommerce_order_id?: number;
  org_id: string;
  [RECEIVER_FIRST_NAME]: string;
  [RECEIVER_LAST_NAME]: string;
  [RECEIVER_COMPANY_NAME]: string;
  [RECEIVER_PHONE]: string;
  [RECEIVER_ADDRESS1]: string;
  [RECEIVER_ADDRESS2]: string;
  [RECEIVER_STATE]: string;
  [RECEIVER_ZIP]: string;
  [RECEIVER_COUNTRY]: string;
  [RECEIVER_CITY]: string;
  [SHIPPING_OPTION]: ShippingOptionsEnum;
  receiver_province: string;
  sender_address1: string;
  sender_address2: string;
  sender_city: string;
  sender_company_name: string;
  sender_country: string;
  sender_email: string;
  sender_first_name: string;
  sender_last_name: string;
  sender_phone: string;
  sender_province: string;
  sender_state: string;
  sender_zip: string;
  ship_carrier: string;
  ship_order_id: number;
  tracking_number: string;
  easypost_id: string;
  package_delivered_date: string;
  shipping_amount: number;
  package_shipped_date: string;
  shipping_cost?: number;
  packaging_cost?: number;
  [TRACKING_URL]: string;
  user_id: string;
  uid: string;
  delayed_shipping_key: string;
  [DELAYED_SHIPPING_URL]: string;
  [DELAYED_SHIPPING_EXPIRATION]: string;
  timezone_metadata?: ITimezoneMetadata | null;
  delayed_shipping_submission_date: string;
  delayed_shipping_page_view_date: string;
  department_id: string;
  status: ReportStatusEnum | string;
  date_to_send: string;
  pick_campaign_ids: string[];
  expiration_date: Date | null;
  items?: IReportEngagementItem[] | null;
  fulfillment_center_names?: string[];
  was_scheduled?: boolean;
  receiver_next_reminder_send_data?: string;
  ship_order_id_str?: string;
  is_receiver_address_fixed: boolean;
  is_office_dropship_enabled?: boolean;
  [DISABLE_EMAIL_NOTIFICATIONS]?: boolean;
  [DISABLE_RECIPIENT_SHIPPED_EMAIL]: boolean;
  [TITLE]?: string;
  marketplace?: TMarketplaceSendMap | null;
}

export interface IReportGiftBudget {
  value: number;
  currency: string;
  used?: number;
}

export enum GiftBudgetUsageKindEnum {
  Pending = 'pending',
  Spent = 'spent',
  NotUsed = 'not_used',
}

export interface IReportGiftBudgetUsage {
  kind: GiftBudgetUsageKindEnum;
  budget: IReportGiftBudget;
  spent: number;
  remaining: number;
}

export interface IReportWithCampaignSummaries extends IReport {
  pick_campaigns: ICampaignSummary[];
  name: undefined;
  description: undefined;
}

export interface IReportsState {
  [ReportTypesEnum.Regular]: { [key: string]: IReportWithCampaignSummaries } | null;
  [ReportTypesEnum.Scheduled]: { [key: string]: IReportWithCampaignSummaries } | null;
  isLoading: boolean;
  isLoadingSingle: boolean;
  isLoadingEdit: boolean;
  error: any;
  isFinal: boolean;
  totalCount: number;
  download: {
    url: string | null;
    isLoading: boolean;
    error: IApiError | null;
  };
}

export interface IReportStatusMapper {
  [k: string]: {
    label: string;
    color: string;
  };
}

export interface IReportsSelectorMetadata {
  filter?: FilterTypesEnum;
  type: ReportTypesEnum;
}

export interface IReportsSelectorMetadataWithUserId extends IReportsSelectorMetadata {
  userId?: string;
}

//#region Request interfaces

export interface IReportsRequest {
  page: number;
  page_size: number;
  first_name?: string;
  company?: string;
  department_ids?: string[];
  search_query?: string;
  date_range?: {
    from: string;
    to: string;
  };
  one_link_id?: string;
  status?: ReportStatusEnum;
  source_type?: ReportSourceTypesEnum;
  fulfillment_center_ids?: string[];
}

export interface IFetchReportsResponse {
  engagements: IReport[];
  scheduled_engagements: IReport[];
  page: number;
  page_size: number;
  total_count: number;
}

export interface IFetchReportsResponsePayload extends IFetchReportsResponse {
  type: ReportTypesEnum;
}

export interface IFetchReportsRequestPayload {
  isOrgSearch?: boolean;
  isDeptSearch?: boolean;
  body: IReportsRequest;
  type: ReportTypesEnum;
  one_link_id?: string;
}

export interface IFetchReportByIdRequestPayload extends IPromiseCallbacks {
  reportId: string;
  type: ReportTypesEnum;
}

export interface IFetchReportByIdResponsePayload {
  engagement: IReport;
  type: ReportTypesEnum;
}

export interface IPopulateReportsCampaignSummariesRequestPayload {
  campaign_ids: string[];
  reportId: string;
  type: ReportTypesEnum;
}

export interface IPopulateReportsCampaignSummariesResponsePayload extends IFetchCampaignSummariesResponsePayload {
  reportId: string;
  type: ReportTypesEnum;
}

export interface IPopulateReportsCampaignSummariesFailurePayload {
  reportId: string;
  type: ReportTypesEnum;
  error: unknown;
}

export interface IDownloadReportsCSVRequestPayload extends Partial<IPromiseCallbacks> {
  type: ReportTypesEnum;
  department_ids?: string[];
  date_range?: {
    from: string | null;
    to: string | null;
  };
  search_query?: string;
  inventory_breakdown?: boolean;
  one_link_id?: string;
  source_type?: ReportSourceTypesEnum;
  item_id?: string;
  campaign_id?: string;
  status?: ReportStatusEnum;
  fulfillment_center_ids?: string[];
}

export type UpdateReportValueRequestPayload<T extends IReport> = {
  [key in keyof Partial<T>]: T[key];
} & {
  type: ReportTypesEnum;
  id: string | undefined;
};

export interface IReportsListFilters {
  query: string;
  tab: ReportTypesEnum;
  sendType: ReportSourceTypesEnum;
  status: ReportStatusEnum | undefined | null;
  selectedDepartments: string | string[] | null;
  startRange: IDatePickerOutput['from'];
  endRange: IDatePickerOutput['to'];
  selectedItemId?: string;
  selectedCampaignId?: string;
  selectedFulfillmentCenterIds: string[];
}

//#endregion
